<template>
  <div class="space-y-4 max-w-xl mx-auto pb-4 sm:pb-8 w-full">
    <!-- Top Step Actions (Skip Step) -->
    <div v-if="canSkip" class="flex justify-end">
      <v-btn text small :color="$vuetify.theme.currentTheme.employeePrimary" class="rounded-lg text-xs font-bold capitalize" @click="skipStep">
        Skip Step
        <v-icon right x-small>mdi-chevron-double-right</v-icon>
      </v-btn>
    </div>

    <!-- Task Customer & Outcome Badge -->
    <div v-if="taskData"
      class="bg-indigo-50/60 rounded-xl p-3 border border-indigo-100 flex items-center justify-between text-xs">
      <div>
        <span class="text-gray-500 block">Customer / Account</span>
        <span class="font-bold text-indigo-950 text-sm">{{ customerName }}</span>
        <div v-if="resolvedMeetingOutcome" class="mt-1 flex items-center gap-1.5">
          <span class="text-[10px] text-gray-500 font-medium">Outcome:</span>
          <span class="bg-indigo-100/80 text-indigo-800 font-bold px-2 py-0.5 rounded text-[11px]">
            {{ resolvedMeetingOutcome }}
          </span>
        </div>
      </div>
      <span class="bg-white text-indigo-700 font-extrabold px-2.5 py-1 rounded-lg border border-indigo-200">
        {{ caseNumber }}
      </span>
    </div>

    <!-- Questions Form Card List -->
    <div class="space-y-3">
      <div v-if="loadingQuestions" class="py-12 bg-white rounded-2xl border border-gray-100 text-center">
        <v-progress-circular indeterminate color="indigo" size="32"></v-progress-circular>
        <p class="text-xs text-gray-500 font-medium mt-2 mb-0">Loading dynamic questionnaire...</p>
      </div>

      <template v-else-if="questions.length > 0">
        <div v-for="(q, index) in questions" :key="q._id || index"
          class="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-gray-100 space-y-2.5 transition-all">
          <!-- Question Title & Index Pill -->
          <div class="flex items-center justify-between pb-1.5 border-b border-gray-100">
            <div class="flex items-center gap-1.5 min-w-0 pr-2">
              <span class="w-2 h-2 rounded-full bg-indigo-500 shrink-0"></span>
              <span class="font-bold text-xs sm:text-sm text-gray-900 leading-tight">
                {{ q.question }}
                <span v-if="q.isRequired" class="text-red-500 font-extrabold">*</span>
              </span>
            </div>
            <span class="text-[10px] font-extrabold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full shrink-0">
              Q{{ index + 1 }}
            </span>
          </div>

          <!-- Question Input Types -->
          <!-- 1. Text Field -->
          <div v-if="q.questionType === 'TEXT' || !q.questionType" class="pt-1">
            <v-text-field v-model="formResponses[index]" outlined dense placeholder="Enter your response..."
              class="rounded-xl text-xs" hide-details="auto"></v-text-field>
          </div>

          <!-- 2. Number Field -->
          <div v-else-if="q.questionType === 'NUMBER'" class="pt-1">
            <v-text-field v-model.number="formResponses[index]" type="number" outlined dense placeholder="0"
              class="rounded-xl text-xs" hide-details="auto"></v-text-field>
          </div>

          <!-- 3. Textarea Field -->
          <div v-else-if="q.questionType === 'TEXTAREA'" class="pt-1">
            <v-textarea v-model="formResponses[index]" rows="2.5" outlined dense
              placeholder="Provide detailed feedback..." class="rounded-xl text-xs" hide-details="auto"></v-textarea>
          </div>

          <!-- 4. Radio / Single Choice Options -->
          <div v-else-if="q.questionType === 'RADIO' && q.options" class="space-y-1 pt-1">
            <v-radio-group v-model="formResponses[index]" column hide-details class="mt-0">
              <v-radio v-for="opt in q.options" :key="opt" :label="opt" :value="opt" color="indigo"
                class="text-xs"></v-radio>
            </v-radio-group>
          </div>

          <!-- 5. Checkbox / Multiple Choice Options -->
          <div v-else-if="q.questionType === 'CHECKBOX' && q.options"
            class="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
            <v-checkbox v-for="opt in q.options" :key="opt" v-model="formResponses[index]" :label="opt" :value="opt"
              color="indigo" hide-details class="mt-0 text-xs"></v-checkbox>
          </div>

          <!-- 6. Attachments / Image Upload -->
          <div v-else-if="q.questionType === 'CLICK_PICTURES' || q.questionType === 'LOCAL_ATTACHMENTS'" class="pt-1">
            <v-file-input v-model="formFiles[index]" prepend-icon="" prepend-inner-icon="mdi-camera-outline" outlined
              dense show-size placeholder="Select or capture image" class="rounded-xl text-xs"
              hide-details></v-file-input>
          </div>
        </div>
      </template>

      <!-- Empty Questions fallback form -->
      <div v-else class="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center space-y-3">
        <v-icon size="36" color="indigo lighten-2">mdi-clipboard-check-outline</v-icon>
        <h3 class="text-sm font-bold text-gray-800 mb-0">No Questionnaire Required</h3>
        <p class="text-xs text-gray-500 max-w-xs mx-auto mb-0">No questions are configured for this visit. You may
          proceed to
          the next step.</p>
      </div>

      <!-- Action Button Footer -->
      <div class="pt-2">
        <v-btn depressed block :color="$vuetify.theme.currentTheme.employeePrimary"
          class="h-11 rounded-xl font-extrabold text-xs sm:text-sm white--text shadow-xs" :loading="submitting"
          @click="submitQuestionnaire">
          <span>Submit</span>
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getTaskById,
  getCollectionCaseById,
  updateTaskStatus,
  getTaskSettings,
  getOrgConfig,
  getExtendUseCaseById,
  getQuestionnaireForm,
  uploadMeetingAttachment
} from '@/app/services/dashboard'
import {
  sendStepLocation,
  computeTaskSteps,
  getNextStepRoute
} from '@/app/utils/taskFlow'

export default {
  name: 'AppTaskQuestionnaire',
  data() {
    return {
      taskId: this.$route.params.taskId,
      isCollection: this.$route.query.isCollection === 'true' || this.$route.query.isCollection === true,
      taskData: null,
      taskSettings: null,
      orgConfig: null,
      orgExtendUseCase: null,
      loadingQuestions: false,
      submitting: false,
      stepsList: [],
      questions: [],
      formResponses: {},
      formFiles: {}
    }
  },
  computed: {
    customerName() {
      return (
        this.taskData?.customer?.customerFullName ||
        this.taskData?.customer?.name ||
        this.taskData?.taskTitle ||
        'Customer'
      )
    },
    caseNumber() {
      return this.taskData?.caseNo ? `#${this.taskData.caseNo}` : 'Task'
    },
    resolvedMeetingOutcome() {
      const cached =
        localStorage.getItem(`lmo-${this.taskId}`) ||
        localStorage.getItem(`lastMeetingOutcome-${this.taskId}`)
      return (
        cached ||
        this.taskData?.meetingDetails?.meetingOutCome ||
        this.taskData?.userDefinedCaseStatus ||
        null
      )
    },
    resolvedOutcomeCategory() {
      return (
        localStorage.getItem('osc') ||
        localStorage.getItem('lastOutcomeCategory') ||
        this.taskData?.caseStatusCategory ||
        null
      )
    },
    currentStepIndex() {
      const idx = this.stepsList.findIndex((s) => s.routeName === 'AppTaskQuestionnaire')
      return idx >= 0 ? idx : 1
    },
    totalStepsCount() {
      return this.stepsList.length || 3
    },
    canSkip() {
      const current = this.stepsList.find((s) => s.routeName === 'AppTaskQuestionnaire')
      return !!current?.canSkip
    }
  },
  mounted() {
    this.initStep()
  },
  methods: {
    async initStep() {
      try {
        this.loadingQuestions = true
        const [taskRes, settingsRes, orgRes] = await Promise.allSettled([
          getTaskById({ taskId: this.taskId, isCollection: this.isCollection })
            .catch(() => (this.isCollection ? getCollectionCaseById({ caseId: this.taskId }) : null)),
          getTaskSettings(),
          getOrgConfig()
        ])
        if (taskRes.status === 'fulfilled' && taskRes.value) {
          const res = taskRes.value?.data
          this.taskData = res?.case || res?.task || res?.data?.case || res?.data?.task || res?.data || res
        }

        if (settingsRes.status === 'fulfilled') {
          this.taskSettings = settingsRes.value.data?.data || settingsRes.value.data
        }

        if (orgRes.status === 'fulfilled') {
          this.orgConfig = orgRes.value.data?.data || orgRes.value.data
        }

        this.stepsList = computeTaskSteps(this.taskSettings, this.orgConfig)

        // Fetch subcase configuration if available
        const subCaseId = this.taskData?.case?.subCase || this.taskData?.subCase
        if (subCaseId) {
          try {
            const extRes = await getExtendUseCaseById(subCaseId)
            this.orgExtendUseCase = extRes.data?.data || extRes.data
          } catch (extErr) {
            console.warn('Could not fetch extendUseCase config:', extErr)
          }
        }

        // Determine questionnaire form ID based on meeting outcome dependency
        let formId = null

        // 1. Check outcome-specific questionnaire from orgExtendUseCase userDefinedCaseStatus
        const outcome = this.resolvedMeetingOutcome
        const outcomeCategory = this.resolvedOutcomeCategory
        const subCase = this.orgExtendUseCase

        if (subCase && outcome) {
          let selectedCat = null
          if (outcomeCategory && Array.isArray(subCase.userDefinedCaseStatus)) {
            selectedCat = subCase.userDefinedCaseStatus.find((el) => el._id === outcomeCategory)
          }
          if (!selectedCat && Array.isArray(subCase.userDefinedCaseStatus)) {
            // Find category that contains this outcome
            selectedCat = subCase.userDefinedCaseStatus.find((el) => {
              if (el.caseOutcomeFormId && el.caseOutcomeFormId[outcome]) return true
              if (el.optionsList && el.optionsList.some((opt) => (opt.code || opt.value || opt) === outcome)) return true
              if (el.options && el.options.includes(outcome)) return true
              return false
            })
          }

          if (selectedCat?.caseOutcomeFormId && selectedCat.caseOutcomeFormId[outcome]) {
            formId = selectedCat.caseOutcomeFormId[outcome]
          }
        }

        // 2. Check collection case / subcase outcome questionnaires array
        if (!formId && subCase && Array.isArray(subCase.outcomeQuestionnaires)) {
          const match = subCase.outcomeQuestionnaires.find((el) => el.meetingOutCome === outcome)
          if (match?.formId) {
            formId = match.formId
          } else if (match?.questionnaire && Array.isArray(match.questionnaire)) {
            this.questions = match.questionnaire
          }
        }

        // 3. Fallback to default questionnaire form from taskSettings
        if (!formId && !this.questions.length) {
          formId = this.taskSettings?.questionnaireDynamicForm || null
        }

        // Fetch dynamic form questions if formId identified
        if (formId) {
          try {
            const formRes = await getQuestionnaireForm(formId)
            const list = formRes.data?.data?.[0]?.questions || formRes.data?.questions || formRes.data?.data || []
            if (Array.isArray(list) && list.length > 0) {
              this.questions = list
            }
          } catch (formErr) {
            console.warn('Could not fetch dynamic questionnaire form:', formErr)
          }
        }

        // Standard fallback questions if no custom questions resolved
        if (!this.questions.length) {
          this.questions = [
            {
              _id: 'q1',
              question: 'Did you meet the customer in person?',
              questionType: 'RADIO',
              isRequired: true,
              options: ['Yes, Met in person', 'No, Met family member', 'No, Door locked / Not available']
            },
            {
              _id: 'q2',
              question: 'Customer Current Employment Status',
              questionType: 'RADIO',
              isRequired: false,
              options: ['Salaried', 'Self-Employed / Business', 'Unemployed', 'Retired']
            },
            {
              _id: 'q3',
              question: 'Customer Feedback / Remarks',
              questionType: 'TEXTAREA',
              isRequired: true
            }
          ]
        }

        // Prepopulate existing responses if available
        const existingQ = this.taskData?.meetingDetails?.questionnaire || []
        this.questions.forEach((q, idx) => {
          const match = existingQ.find((eq) => eq.question === q.question || eq._id === q._id)
          if (match?.userResponse) {
            this.$set(this.formResponses, idx, match.userResponse)
          } else if (q.questionType === 'CHECKBOX') {
            this.$set(this.formResponses, idx, [])
          }
        })
      } catch (err) {
        console.error('Error initializing questionnaire step:', err)
      } finally {
        this.loadingQuestions = false
      }
    },
    async submitQuestionnaire() {
      // Validate mandatory questions
      for (let i = 0; i < this.questions.length; i++) {
        const q = this.questions[i]
        const val = this.formResponses[i]
        if (q.isRequired && (val === undefined || val === null || val === '')) {
          this.$toast?.error?.(`Please answer mandatory question: "${q.question}"`)
          return
        }
      }

      try {
        this.submitting = true

        const validTaskId =
          this.taskData?._id ||
          this.taskData?.taskId ||
          this.taskData?.case?.tasks?.[0]?._id ||
          this.taskId

        // Handle attachment uploads if any file selected
        const uploads = []
        for (let i = 0; i < this.questions.length; i++) {
          const file = this.formFiles[i]
          if (file) {
            const formData = new FormData()
            formData.append('taskId', validTaskId)
            formData.append('attachments', file)
            const upRes = await uploadMeetingAttachment(formData).catch(() => null)
            const path = upRes?.data?.uploads?.[0]?.doc_physical_path
            if (path) {
              uploads[i] = path
            }
          }
        }

        const formattedResponses = this.questions.map((q, i) => ({
          ...q,
          userResponse: uploads[i] || this.formResponses[i] || ''
        }))

        // Extract existing meetingDetails if present and format properly
        let existingMeeting = this.taskData?.meetingDetails
        if (typeof existingMeeting === 'string') {
          try {
            existingMeeting = JSON.parse(existingMeeting)
          } catch (e) {
            existingMeeting = {}
          }
        }
        if (!existingMeeting || typeof existingMeeting !== 'object') {
          existingMeeting = {}
        }

        const payload = {
          meetingDetails: {
            ...existingMeeting,
            questionnaire: formattedResponses
          }
        }

        await updateTaskStatus({
          taskId: validTaskId,
          isCollection: this.isCollection,
          ...payload
        })

        this.$toast?.success?.('Questionnaire responses saved!')

        // Send GPS coordinate on submit
        await sendStepLocation('AppTaskQuestionnaire', this.taskData)

        this.proceedToNext()
      } catch (err) {
        console.error('Error saving questionnaire responses:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to submit questionnaire')
      } finally {
        this.submitting = false
      }
    },
    skipStep() {
      this.proceedToNext()
    },
    proceedToNext() {
      const nextRouteName = getNextStepRoute('AppTaskQuestionnaire', this.stepsList, this.taskData)
      this.$router.push({
        name: nextRouteName,
        params: { taskId: this.taskId },
        query: { isCollection: this.isCollection }
      })
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>
